import { useEffect, useState } from "react";
import {
    useCustom,
    useCustomMutation,
    useInvalidate,
    useList,
    useTranslate,
    useUpdate,
} from "@refinedev/core";
import { message } from "antd";
import { OrderStatus } from "../../../models/order";

interface UseOrderAssignmentParams {
    record: any;
    isAdmin: boolean;
    isTuner: boolean;
    user: any;
    apiUrl: string;
}

export const useOrderAssignment = ({
    record,
    isAdmin,
    isTuner,
    user,
    apiUrl,
}: UseOrderAssignmentParams) => {
    const _t = useTranslate();
    const { mutate: update } = useUpdate();
    const { mutate: retryExternalPushMutation } = useCustomMutation();
    const invalidate = useInvalidate();

    const [assignedUserId, setAssignedUserId] = useState<number | undefined>(record?.assignedUserId);
    const [assignedServiceProviderId, setAssignedServiceProviderId] = useState<number | undefined>(
        record?.serviceProviderId
    );
    const [assignedGroupId, setAssignedGroupId] = useState<number | undefined>(record?.assignedGroupId);
    const [assignmentModalVisible, setAssignmentModalVisible] = useState(false);
    const [isAssigningToMe, setIsAssigningToMe] = useState(false);
    const [productMappingModalOpen, setProductMappingModalOpen] = useState(false);
    const [isRetryingExternalPush, setIsRetryingExternalPush] = useState(false);
    const [isExternalPushSynced, setIsExternalPushSynced] = useState(false);

    const { data: usersData } = useList({
        resource: "users",
        queryOptions: { enabled: isAdmin },
    });

    const { data: serviceProvidersData, refetch: refetchServiceProviders } = useList({
        resource: "service-providers",
        queryOptions: { enabled: isAdmin },
    });

    const { data: groupsData } = useList({
        resource: "user-groups",
        queryOptions: { enabled: isAdmin },
    });

    const shouldPollExternalPush =
        isAdmin &&
        !!record?.id &&
        !!assignedServiceProviderId &&
        record?.assignmentStatus === "assigned";

    const { data: externalPushStatusResponse, refetch: refetchExternalPushStatus } =
        useCustom<{
            serviceProviderId: number;
            status: string;
            attemptsMade: number | null;
            maxAttempts: number | null;
            canRetry: boolean;
            lastError: string | null;
            externalOrderId: string | null;
            ackedAt: string | null;
            unmappedProductIds: number[];
        }>({
            url: `${apiUrl}/orders/${record?.id}/external-push/status`,
            method: "get",
            queryOptions: {
                enabled: shouldPollExternalPush,
                refetchInterval: shouldPollExternalPush && !isExternalPushSynced ? 5000 : false,
            },
        });

    const externalPushStatus = externalPushStatusResponse?.data ?? null;

    useEffect(() => {
        setIsExternalPushSynced(externalPushStatus?.status === "synced");
    }, [externalPushStatus?.status]);

    useEffect(() => {
        setIsExternalPushSynced(false);
    }, [assignedServiceProviderId, record?.id]);

    const { data: myGroupsResponse } = useCustom<{ data: { id: number }[] }>({
        url: `${apiUrl}/user-groups/my-groups`,
        method: "get",
        queryOptions: {
            enabled: isTuner && !!record?.assignedGroupId,
        },
    });

    const myGroups = myGroupsResponse?.data ?? [];
    const userBelongsToAssignedGroup =
        !!record?.assignedGroupId &&
        Array.isArray(myGroups) &&
        (myGroups as { id: number }[]).some((g) => g.id === record.assignedGroupId);

    const canShowAssignToMe =
        (isAdmin && record?.status === OrderStatus.submitted) ||
        (isTuner &&
            !record?.assignedUserId &&
            record?.status === OrderStatus.submitted &&
            userBelongsToAssignedGroup);

    useEffect(() => {
        setAssignedUserId(record?.assignedUserId);
        setAssignedServiceProviderId(record?.serviceProviderId);
        setAssignedGroupId(record?.assignedGroupId);
    }, [record]);

    const handleUserAssignment = (userId: number | undefined) => {
        if (!userId) {
            update(
                {
                    resource: "orders",
                    values: { assignedUserId: null, serviceProviderId: null, assignmentStatus: "pending" },
                    id: record?.id,
                },
                {
                    onSuccess: () => {
                        setAssignedUserId(undefined);
                        setAssignedServiceProviderId(undefined);
                        invalidate({ resource: "orders", invalidates: ["detail"] });
                    },
                    onError: () => {
                        message.error(_t("orders.titles.failedToAssignUser"));
                    },
                }
            );
            return;
        }

        update(
            {
                resource: "orders",
                values: {
                    assignedUserId: userId,
                    serviceProviderId: null,
                    assignmentStatus: "assigned",
                },
                id: record?.id,
            },
            {
                onSuccess: () => {
                    setAssignedUserId(userId);
                    setAssignedServiceProviderId(undefined);
                    invalidate({ resource: "orders", invalidates: ["detail"] });
                    message.success(_t("orders.titles.userAssigned"));
                },
                onError: () => {
                    message.error(_t("orders.titles.failedToAssignUser"));
                    setAssignedUserId(record?.assignedUserId);
                },
            }
        );
    };

    const handleServiceProviderAssignment = (providerId: number | undefined) => {
        if (!providerId) {
            update(
                {
                    resource: "orders",
                    values: { assignedUserId: null, serviceProviderId: null, assignmentStatus: "pending" },
                    id: record?.id,
                },
                {
                    onSuccess: () => {
                        setAssignedUserId(undefined);
                        setAssignedServiceProviderId(undefined);
                        invalidate({ resource: "orders", invalidates: ["detail"] });
                    },
                    onError: () => {
                        message.error(_t("orders.titles.failedToAssignServiceProvider"));
                    },
                }
            );
            return;
        }

        update(
            {
                resource: "orders",
                values: {
                    assignedUserId: null,
                    serviceProviderId: providerId,
                    assignmentStatus: "assigned",
                },
                id: record?.id,
            },
            {
                onSuccess: () => {
                    setAssignedUserId(undefined);
                    setAssignedServiceProviderId(providerId);
                    invalidate({ resource: "orders", invalidates: ["detail"] });
                    message.success(_t("orders.titles.serviceProviderAssigned"));
                },
                onError: () => {
                    message.error(_t("orders.titles.failedToAssignServiceProvider"));
                    setAssignedServiceProviderId(record?.serviceProviderId);
                },
            }
        );
    };

    const handleAssignToMe = () => {
        const userId = (user as any)?.id;
        if (!record?.id || userId == null) return;
        setIsAssigningToMe(true);
        update(
            {
                resource: "orders",
                id: record.id,
                values: {
                    assignedUserId: userId,
                    assignedGroupId: null,
                    serviceProviderId: null,
                    assignmentStatus: "assigned",
                },
            },
            {
                onSuccess: () => {
                    setAssignedUserId(userId);
                    setAssignedGroupId(undefined);
                    setAssignedServiceProviderId(undefined);
                    invalidate({ resource: "orders", invalidates: ["detail"] });
                    message.success(_t("orders.titles.userAssigned"));
                    setIsAssigningToMe(false);
                },
                onError: () => {
                    message.error(_t("orders.titles.failedToAssignUser"));
                    setIsAssigningToMe(false);
                },
            }
        );
    };

    const handleRetryExternalPush = () => {
        if (!record?.id) {
            return;
        }
        setIsRetryingExternalPush(true);
        retryExternalPushMutation(
            {
                url: `${apiUrl}/orders/${record.id}/external-push/retry`,
                method: "post",
                values: {},
            },
            {
                onSuccess: () => {
                    message.success(_t("orders.externalPush.retrySuccess"));
                    setIsRetryingExternalPush(false);
                    setIsExternalPushSynced(false);
                    void refetchExternalPushStatus();
                },
                onError: (error: any) => {
                    const unmapped = error?.response?.data?.unmappedProductIds;
                    if (Array.isArray(unmapped) && unmapped.length > 0) {
                        message.error(_t("orders.externalPush.unmappedStill"));
                    } else {
                        message.error(_t("orders.externalPush.retryError"));
                    }
                    setIsRetryingExternalPush(false);
                },
            }
        );
    };

    const handleProductMappingSuccess = async () => {
        await refetchServiceProviders();
        invalidate({ resource: "service-providers", invalidates: ["list", "detail"] });
        setProductMappingModalOpen(false);
        handleRetryExternalPush();
    };

    return {
        assignedUserId,
        assignedServiceProviderId,
        assignedGroupId,
        assignmentModalVisible,
        setAssignmentModalVisible,
        canShowAssignToMe,
        isAssigningToMe,
        usersData,
        serviceProvidersData,
        groupsData,
        handleUserAssignment,
        handleServiceProviderAssignment,
        handleAssignToMe,
        setAssignedUserId,
        setAssignedServiceProviderId,
        setAssignedGroupId,
        productMappingModalOpen,
        setProductMappingModalOpen,
        isRetryingExternalPush,
        handleRetryExternalPush,
        handleProductMappingSuccess,
        externalPushStatus,
        refetchExternalPushStatus,
    };
};

